'use client';

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Share controls for the AI business page: "Copy link" + "QR code".
 * Minimal client island — the QR SVG is generated on the server and passed
 * in as a string, so no QR library ships to the browser.
 */
export function AiBusinessPageShare({
  url,
  qrSvg,
  businessName,
}: {
  /** Canonical production URL of this business page (the value the QR encodes). */
  url: string;
  /** Pre-rendered QR code as an inline SVG string. */
  qrSvg: string;
  businessName: string;
}) {
  const [copied, setCopied] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Overlays portal to <body>. They only ever render after a client
  // interaction (copy/open), so `document` is guaranteed present then.
  const canPortal = typeof document !== 'undefined';

  // Display form of the URL (no protocol) for the buttons/modal caption.
  const displayUrl = url.replace(/^https?:\/\//, '');

  useEffect(() => {
    return () => {
      if (copyTimer.current) clearTimeout(copyTimer.current);
    };
  }, []);

  useEffect(() => {
    if (!qrOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setQrOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [qrOpen]);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      /* clipboard may be unavailable (insecure context) — still show feedback */
    }
    setCopied(true);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 2200);
  }

  return (
    <>
      <div className="abp-actions">
        <button
          type="button"
          className="abp-btn"
          onClick={handleCopy}
          data-testid="abp-copy-link"
          aria-live="polite"
        >
          {copied ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M20 6 9 17l-5-5" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
          )}
          {copied ? 'Copied!' : 'Copy link'}
        </button>

        <button
          type="button"
          className="abp-btn"
          onClick={() => setQrOpen(true)}
          data-testid="abp-qr-open"
          aria-haspopup="dialog"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <path d="M14 14h3v3M21 14v.01M14 21h3M21 17v4" />
          </svg>
          QR code
        </button>
      </div>

      {canPortal && copied &&
        createPortal(
          <div className="abp-toast" role="status">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M20 6 9 17l-5-5" />
            </svg>
            Link copied to clipboard
          </div>,
          document.body,
        )}

      {canPortal && qrOpen &&
        createPortal(
          // Backdrop click-to-dismiss is a pointer convenience; keyboard users
          // dismiss via Escape (handled above) or the focusable "Done" button.
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
          <div
            className="abp-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`QR code for ${businessName}`}
            onClick={(e) => {
              if (e.target === e.currentTarget) setQrOpen(false);
            }}
          >
            <div className="abp-sheet">
              <h2 className="abp-sheet__title">Scan to open</h2>
              <p className="abp-sheet__sub">Point a phone camera at the code</p>
              <div
                className="abp-qr-card"
                /* QR SVG is generated server-side from a trusted, app-built URL. */
                dangerouslySetInnerHTML={{ __html: qrSvg }}
              />
              <div className="abp-url">{displayUrl}</div>
              <button type="button" className="abp-close" onClick={() => setQrOpen(false)}>
                Done
              </button>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
